import { useL } from "@/lib/useL";
import React, { useState } from "react";
import { ShareDialog } from "@/components/ShareDialog";
import { toast } from "sonner";
import { Send, Printer, Linkedin } from "lucide-react";
import { trackShare } from "@/lib/analytics";

/**
 * Smart share + print action buttons.
 *
 * Props:
 * - title:        string — used for the native share sheet
 * - text:         string — the summary body (plain, shared/copied)
 * - isPublic:     boolean — whether the summary already has a public URL
 * - publicUrl:    string — the public URL (only meaningful when isPublic is true,
 *                          BUT we also pass it for the "will be enabled" path)
 * - onEnsurePublic: async function — toggles the summary to public (returns void)
 * - sharing:      boolean — parent is currently toggling public state
 * - testidPrefix: string
 *
 * Behavior:
 * - Click PARTAGER on a PUBLIC summary → share title + text + publicUrl directly.
 * - Click PARTAGER on a PRIVATE summary → ask the user:
 *     "Rendre ce résumé public pour permettre au destinataire de le lire en entier ?"
 *   YES → toggle public then share with URL.
 *   NO  → share title + text only (no URL).
 */
export function ShareAndPrint({
  title,
  text,
  isPublic = false,
  publicUrl = null,
  onEnsurePublic = null,
  sharing = false,
  testidPrefix = "action",
  needsAccount = false,
}) {
  const L = useL();
  const [open, setOpen] = useState(false);

  const onPrint = () => {
    trackShare("print");
    if (typeof window !== "undefined") window.print();
  };

  // 1-click LinkedIn share. Auto-publishes the summary if needed, then opens
  // LinkedIn's share-offsite popup with the public URL — LinkedIn scrapes
  // the OG image and title we already serve at /api/share/{id}, so the
  // post gets a rich card ("Made with SUX1" watermark included in the OG
  // image + landing footer).
  const onLinkedInShare = async () => {
    const openLinkedIn = (u) => {
      const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}`;
      window.open(shareUrl, "_blank", "noopener,noreferrer,width=720,height=640");
      trackShare("linkedin");
    };
    if (isPublic && publicUrl) {
      openLinkedIn(publicUrl);
      return;
    }
    if (!onEnsurePublic || !publicUrl) {
      toast.error(L("Lien public indisponible pour ce résumé.", "Public link unavailable for this summary."));
      return;
    }
    try {
      await onEnsurePublic();
      openLinkedIn(publicUrl);
      toast.success(L("Résumé publié — LinkedIn ouvert avec l'aperçu.", "Summary published — LinkedIn opened with the preview."));
    } catch {
      toast.error(L("Impossible de générer le lien public.", "Could not create the public link."));
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        disabled={sharing}
        data-testid={`${testidPrefix}-share`}
        className="font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 border border-border hover:bg-[#FF2A00] hover:text-white hover:border-[#002FA7] transition-colors disabled:opacity-50 flex items-center gap-2 no-print"
        title={
          isPublic
            ? L("Partager : lien, email, WhatsApp, copie…", "Share: link, email, WhatsApp, copy…")
            : L("Partager : email, WhatsApp, copie… (avec option lien public)", "Share: email, WhatsApp, copy… (with public link option)")
        }
      >
        <Send className="w-3.5 h-3.5" /> {L("Partager", "Share")}
      </button>
      {onEnsurePublic && publicUrl && (
        <button
          onClick={onLinkedInShare}
          disabled={sharing}
          data-testid={`${testidPrefix}-linkedin`}
          className="font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 border border-[#0A66C2] text-[#0A66C2] hover:bg-[#0A66C2] hover:text-white transition-colors disabled:opacity-50 flex items-center gap-2 no-print"
          title={L("Publier ce résumé sur LinkedIn (rend le résumé public)", "Post this summary on LinkedIn (makes it public)")}
        >
          <Linkedin className="w-3.5 h-3.5" /> LinkedIn
        </button>
      )}
      <button
        onClick={onPrint}
        data-testid={`${testidPrefix}-print`}
        data-tip={isPublic ? L("Imprimer : un QR code vers la version en ligne est ajouté en bas de la fiche.", "Print: a QR code to the online version is added at the bottom.") : L("Imprimer. Astuce : créez d'abord le lien public (Partager) pour ajouter un QR code en bas de la fiche.", "Print. Tip: create the public link first (Share) to add a QR code at the bottom.")}
        className="font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 border border-border hover:bg-[#FF2A00] hover:text-white hover:border-[#002FA7] transition-colors flex items-center gap-2 no-print"
        title={L("Imprimer / Enregistrer en PDF", "Print / Save as PDF")}
      >
        <Printer className="w-3.5 h-3.5" /> {L("Imprimer", "Print")}
      </button>
      <ShareDialog
        open={open}
        onOpenChange={setOpen}
        title={title || "SUX1"}
        text={text || ""}
        isPublic={isPublic}
        publicUrl={publicUrl}
        onEnsurePublic={onEnsurePublic && publicUrl ? onEnsurePublic : null}
        sharing={sharing}
        needsAccount={needsAccount}
      />
    </>
  );
}

export default ShareAndPrint;
